<template>
  <SectionPageShell
    section="survey"
    title="Wir richten Ihren Eintrag Schritt für Schritt ein"
    subtitle="Fragebogen"
  >
  <section class="py-2 py-lg-3">
    <div class="container">
      <div class="row">
        <div class="col-12">
          <div class="survey-shell">
            <div class="survey-shell__header mb-4 mb-lg-5">
              <div class="d-flex flex-column flex-lg-row align-items-lg-end justify-content-between gap-3">
                <div>
                  <p class="text-secondary mb-0">
                    Wenige Angaben genügen, damit wir Ihre Vermarktungsstruktur passend vorbereiten.
                  </p>
                </div>
                <div v-if="showProgressHeader" class="d-flex flex-column flex-lg-row gap-3">
                  <div class="survey-step-chip" :class="{ 'survey-step-chip--completed': setupPhaseCompleted }">
                    <div class="text-uppercase small fw-semibold text-secondary mb-1">Schritt 1</div>
                    <div class="h5 mb-1 d-flex align-items-center gap-2">
                      <Icon v-if="setupPhaseCompleted" icon="fa-pro-solid:circle-check" class="text-success" />
                      <span>Persönliche Daten</span>
                    </div>
                    <div class="small text-secondary">{{ setupChipSubtitle }}</div>
                  </div>
                  <div
                    class="survey-step-chip"
                    :class="{
                      'survey-step-chip--completed': objectCreationPhaseCompleted,
                      'survey-step-chip--active': isObjectCreationPhase && !objectCreationPhaseCompleted,
                    }"
                  >
                    <div class="text-uppercase small fw-semibold text-secondary mb-1">Schritt 2</div>
                    <div class="h5 mb-1 d-flex align-items-center gap-2">
                      <Icon v-if="objectCreationPhaseCompleted" icon="fa-pro-solid:circle-check" class="text-success" />
                      <span>{{ objectCreationChipTitle }}</span>
                    </div>
                    <div class="small text-secondary">{{ objectCreationChipSubtitle }}</div>
                  </div>
                </div>
              </div>

              <div v-if="showProgressHeader" class="survey-progress card border-0 shadow-sm mt-4">
                <div class="card-body p-3 p-lg-4">
                  <div class="d-flex flex-column flex-md-row justify-content-between gap-2 mb-3">
                    <div>
                      <div class="small text-uppercase fw-semibold text-secondary mb-1">Aktueller Abschnitt</div>
                      <div class="fw-semibold">{{ currentStageLabel }}</div>
                    </div>
                    <div class="text-md-end">
                      <div class="small text-uppercase fw-semibold text-secondary mb-1">Abschluss</div>
                      <div class="fw-semibold">{{ progressPercent }}%</div>
                    </div>
                  </div>
                  <div class="progress" role="progressbar" :aria-valuenow="progressPercent" aria-valuemin="0" aria-valuemax="100">
                    <div class="progress-bar progress-bar-striped" :style="{ width: `${progressPercent}%` }"></div>
                  </div>
                </div>
              </div>
            </div>

            <Form
              :key="surveyFormKey"
              @submit="nextStep"
              v-slot="{ errors, submitCount }"
              :validation-schema="surveyStore.currentConfig.schema"
              class="survey-form"
              ref="surveyForm"
            >
              <div v-if="duplicateNameError" class="alert alert-warning border-0 mb-4">
                <div class="fw-semibold mb-2">Der Objektname ist bereits vergeben.</div>
                <div class="text-secondary mb-3">
                  Bitte wählen Sie direkt hier einen anderen Namen und senden Sie den Fragebogen anschließend erneut ab. Beispielsweise können Sie einfach den Ortsnamen ergänzen falls noch nicht vorhanden.
                </div>
                <label for="duplicate-objektname" class="form-label fw-semibold">Neuer Objektname</label>
                <input
                  id="duplicate-objektname"
                  v-model="surveyStore.data.objektname"
                  type="text"
                  class="form-control"
                  :class="{'is-invalid': errors.objektname}"
                  autocomplete="off"
                >
                <div v-if="errors.objektname" class="invalid-feedback d-block">
                  {{ errors.objektname }}
                </div>
              </div>
              <component
                :is="surveyStore.currentConfig.component"
                :errors="submitCount > 0 ? errors : {}"
                :show-errors="submitCount > 0"
              />

              <div v-if="showNavigation" class="survey-nav mt-4 mt-lg-5">
                <div class="survey-nav__surface">
                  <details
                    v-if="submitCount > 0 && validationErrorEntries(errors).length > 1"
                    class="survey-nav__error-disclosure d-sm-none mb-3"
                    aria-live="polite"
                  >
                    <summary class="survey-nav__error-summary">
                      <Icon icon="fa-pro-solid:circle-exclamation" aria-hidden="true" />
                      <span>{{ validationErrorSummary(errors) }}</span>
                    </summary>
                    <div class="survey-nav__error-details">
                      <ul class="mb-0 ps-3">
                        <li v-for="([field, message]) in validationErrorEntries(errors)" :key="field">
                          {{ message }}
                        </li>
                      </ul>
                    </div>
                  </details>
                  <div
                    v-if="submitCount > 0 && validationErrorEntries(errors).length === 1"
                    class="survey-nav__single-error d-sm-none mb-3"
                    role="alert"
                  >
                    <Icon icon="fa-pro-solid:circle-exclamation" aria-hidden="true" />
                    <span>{{ validationErrorEntries(errors)[0][1] }}</span>
                  </div>
                  <div
                    v-if="submitCount > 0 && validationErrorEntries(errors).length"
                    class="survey-nav__errors alert alert-danger d-none d-sm-block mb-3"
                    role="alert"
                  >
                    <div class="fw-semibold mb-1">Bitte prüfen Sie Ihre Angaben</div>
                    <ul class="mb-0 ps-3">
                      <li v-for="([field, message]) in validationErrorEntries(errors)" :key="field">
                        {{ message }}
                      </li>
                    </ul>
                  </div>
                  <div class="survey-nav__main">
                    <div class="survey-nav__next">
                      <div class="survey-nav__eyebrow">Nächster Schritt</div>
                      <div class="survey-nav__title">{{ nextStepLabel }}</div>
                    </div>
                    <div class="survey-nav__controls">
                      <button
                        type="button"
                        class="btn btn-outline-secondary survey-nav__direction-button"
                        aria-label="Zurück"
                        @click="prevStep()"
                        :disabled="isSubmitting"
                      >
                        <Icon icon="fa-pro-solid:arrow-left" class="d-sm-none" aria-hidden="true" />
                        <span class="d-none d-sm-inline">Zurück</span>
                      </button>
                      <button
                        v-if="showSubmit"
                        type="button"
                        class="btn btn-primary survey-nav__direction-button"
                        aria-label="Fragebogen absenden"
                        @click="sendForm"
                        :disabled="isSubmitting"
                      >
                        <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-sm-2" aria-hidden="true"></span>
                        <Icon v-else icon="fa-pro-solid:paper-plane" class="d-sm-none" aria-hidden="true" />
                        <span class="d-none d-sm-inline">{{ isSubmitting ? 'Wird gesendet...' : 'Fragebogen absenden' }}</span>
                      </button>
                      <button
                        v-else
                        class="btn btn-primary survey-nav__direction-button"
                        type="submit"
                        aria-label="Weiter"
                        :disabled="isSubmitting"
                      >
                        <Icon icon="fa-pro-solid:arrow-right" class="d-sm-none" aria-hidden="true" />
                        <span class="d-none d-sm-inline">Weiter</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </div>
  </section>
  </SectionPageShell>
</template>

<script>
import SectionPageShell from "@/components/base_type/SectionPageShell.vue";
import {useUserStore} from "@/stores/user";
import {useSurveyStore} from "@/stores/survey";
import {Form} from 'vee-validate';
import Swal from "sweetalert2";

export default {
  components: {
    Form,
    SectionPageShell,
  },
  setup() {
    return {
      userStore: useUserStore(),
      surveyStore: useSurveyStore(),
    }
  },
  data() {
    return {
      showNavSubmit: false,
      isSubmitting: false,
      duplicateNameError: false,
    }
  },
  created() {
    this.surveyStore.reset()
  },
  methods: {
    buildSurveyPayload() {
      const st = this.surveyStore.data.standort
      let standortPayload = null
      if (st && typeof st === 'object' && Object.keys(st).length) {
        const id = st.id
        if (id != null && id !== '' && !Number.isNaN(Number(id))) {
          standortPayload = Number(id)
        } else {
          standortPayload = {...st}
        }
      }
      return {
        ...this.surveyStore.data,
        address: this.surveyStore.data.address ? {...this.surveyStore.data.address} : {},
        standort: standortPayload,
      }
    },
    getStepName() {
      const step = this.surveyStore.currentConfig.steps?.[this.surveyStore.currentStep]
      if (!step) {
        return false
      }

      return Object.entries(step).find(([, value]) => value?.component)?.[0] || false
    },
    nextStep() {
      this.duplicateNameError = false
      if (this.surveyStore.currentConfig.name === 'personal_data') {
        this.surveyStore.setCurrentConfig('objekttyp')
      } else if (this.surveyStore.currentConfig.name === 'objekttyp') {
        this.surveyStore.setCurrentConfig(this.surveyStore.data.objekttyp)
      } else {
        if (this.surveyStore.currentStep + 1 < this.surveyStore.currentConfig.steps.length) {
          this.surveyStore.currentStep++
          this.surveyStore.setSchema()
        }
        if (this.surveyStore.currentStep + 2 > this.surveyStore.currentConfig.steps.length) {
          this.showNavSubmit = true
        }
      }
    },
    prevStep() {
      this.duplicateNameError = false
      this.showNavSubmit = false
      if (this.surveyStore.currentStep - 1 >= 0) {
        this.surveyStore.currentStep--
        this.surveyStore.setSchema()
      } else {
        if (this.surveyStore.currentConfig.name === 'personal_data') {
          return
        } else if (this.surveyStore.currentConfig.name === 'objekttyp') {
          this.surveyStore.setCurrentConfig('personal_data')
        } else {
          //jump back to starting point
          this.surveyStore.setCurrentConfig('objekttyp')
        }
      }
    },
    sendForm(e) {
      e.preventDefault()
      if (this.isSubmitting) {
        return
      }

      this.duplicateNameError = false
      this.isSubmitting = true
      return this.surveyStore.apiClientV2.post(`/survey`, {survey: {data: this.buildSurveyPayload()}}).then(async ({data}) => {
        if (data.error) {
          if (this.handleNameConflicts(data)) {
            return
          }
          Swal.fire({
            title: 'Error!',
            text: JSON.stringify(data.message),
            icon: 'error',
            confirmButtonText: 'Ok'
          })
        } else {
          try {
            await this.userStore.apiUserData()
            this.userStore.fetchMyClients(true)
          } catch (refreshError) {
            console.error('survey completion refresh failed', refreshError)
          }
          this.userStore.user.markSurveyCompleted()
          this.surveyStore.setCreatedObjects(data.object || null, data.additional_objects || [], data.clients || [])
          this.surveyStore.passedSteps = (this.surveyStore.currentConfig?.steps?.length || 0) + 2
          this.created = true; // braucht das jemand?
          this.surveyStore.setCurrentConfig('outro')
          this.surveyStore.setSchema()
        }
      }).catch((error) => {
        const response = error?.response
        if (response?.data?.name_conflicts && this.handleNameConflicts(response.data)) {
          return
        }
        if (response?.status === 422 && response?.data?.errors) {
          const normalizedErrors = this.normalizeBackendErrors(response.data.errors)
          this.duplicateNameError = Boolean(normalizedErrors.objektname?.includes('bereits vergeben'))
          this.$refs.surveyForm?.setErrors(normalizedErrors)
          return
        }

        throw error
      }).finally(() => {
        this.isSubmitting = false
      })
    },
    normalizeBackendErrors(errors = {}) {
      return Object.fromEntries(
        Object.entries(errors).map(([field, message]) => {
          const normalizedField = field === 'name' ? 'objektname' : field
          if (Array.isArray(message)) {
            return [normalizedField, message.join(', ')]
          }
          return [normalizedField, message]
        })
      )
    },
    validationErrorEntries(errors = {}) {
      return Object.entries(errors)
        .filter(([, message]) => Boolean(message))
        .map(([field, message]) => [
          field,
          Array.isArray(message) ? message.join(', ') : message,
        ])
    },
    validationErrorSummary(errors = {}) {
      const count = this.validationErrorEntries(errors).length
      return count === 1 ? '1 Angabe prüfen' : `${count} Angaben prüfen`
    },
    handleNameConflicts(payload = {}) {
      const conflicts = Array.isArray(payload.name_conflicts) ? payload.name_conflicts : []
      if (!conflicts.length) {
        return false
      }

      let hasFocusedTarget = false
      for (const conflictName of conflicts) {
        const target = this.findNameConflictTarget(conflictName)
        if (!target) {
          continue
        }

        this.jumpToStep(target.stepKey)
        this.focusFieldById(target.fieldId)
        hasFocusedTarget = true
        break
      }

      Swal.fire({
        title: 'Name bereits vergeben',
        text: 'Bitte passen Sie den markierten Namen an und senden Sie den Fragebogen erneut.',
        icon: 'warning',
        confirmButtonText: 'Ok'
      })

      return hasFocusedTarget
    },
    findNameConflictTarget(conflictName) {
      if (!conflictName) {
        return null
      }

      if (this.surveyStore.data.objektname === conflictName) {
        this.duplicateNameError = true
        return {
          stepKey: 'objektname',
          fieldId: 'objektname',
        }
      }

      const additionalMatch = Object.entries(this.surveyStore.data.zusatzobjekte || {})
        .find(([, value]) => value && typeof value === 'object' && value.name === conflictName)

      if (additionalMatch) {
        const [key] = additionalMatch
        return {
          stepKey: 'zusatzobjekte',
          fieldId: `${key}_zusatz_name`,
        }
      }

      return null
    },
    jumpToStep(stepKey) {
      const steps = this.surveyStore.currentConfig?.steps || []
      const targetStepIndex = steps.findIndex(step => Object.prototype.hasOwnProperty.call(step, stepKey))
      if (targetStepIndex < 0) {
        return
      }

      this.surveyStore.currentStep = targetStepIndex
      this.surveyStore.setSchema()
      this.showNavSubmit = false
    },
    focusFieldById(fieldId) {
      this.$nextTick(() => {
        const field = document.getElementById(fieldId)
        if (!field) {
          return
        }
        field.scrollIntoView({behavior: 'smooth', block: 'center'})
        field.focus()
      })
    },
    formatStageLabel(label) {
      const map = {
        intro: 'Einführung',
        personal_data: 'Persönliche Daten',
        outro: 'Abschluss',
        objekttyp: 'Art des Eintrags',
        unterkunft: 'Unterkunft',
        essen_trinken: 'Gastronomie',
        freizeitanbieter: 'Freizeitangebot',
        veranstaltung: 'Veranstaltung',
        kunst_kultur: 'Kunst und Kultur',
        standort: 'Standort',
        objektname: 'Objektname',
        zusatzunterkunft: this.accommodationStepLabel,
        weitere_unterkuenfte: this.accommodationStepLabel,
        zusatzobjekte: 'Zusatzobjekte',
        urlaubsarten: 'Urlaubsarten',
        besonderes_gebaeude: 'Besonderheiten',
        unterkunfts_typ: 'Art des Objekts',
        lokal_typ: 'Art des Lokals',
        veranstaltung_kategorie: 'Kategorie',
        veranstaltung_terminart: 'Terminart',
        veranstaltung_termin: 'Termin',
        veranstaltung_endtermin: 'Enddatum',
        veranstaltung_uhrzeit: 'Uhrzeit',
        veranstaltung_ort: 'Veranstaltungsort',
        freizeitanbieter_weitere: 'Weitere Angebote',
        kunst_kultur_kategorie: 'Kategorie',
        selbstbeschreibung: 'Beschreibung',
      }

      if (map[label]) {
        return map[label]
      }

      return label
        ?.split('_')
        .filter(Boolean)
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ') || 'Fragebogen'
    },
  },

  computed: {
    surveyFormKey() {
      return `${this.surveyStore.currentConfig?.name || 'survey'}:${this.surveyStore.currentStep}`
    },
    isIntro() {
      return this.surveyStore.currentConfig.name === 'intro'
    },
    isOutro() {
      return this.surveyStore.currentConfig.name === 'outro'
    },
    showNavigation() {
      return !['outro'].includes(this.surveyStore.currentConfig.name)
    },
    showProgressHeader() {
      return !['intro'].includes(this.surveyStore.currentConfig.name)
    },
    isPersonalDataPhase() {
      return this.surveyStore.currentConfig.name === 'personal_data'
    },
    isObjectTypePhase() {
      return this.surveyStore.currentConfig.name === 'objekttyp'
    },
    isObjectCreationPhase() {
      return !this.isPersonalDataPhase && !this.isObjectTypePhase && !this.isOutro
    },
    setupPhaseCompleted() {
      return this.isObjectCreationPhase || this.isOutro
    },
    objectCreationPhaseCompleted() {
      return this.isOutro
    },
    totalSteps() {
      if (this.isOutro) {
        return Math.max(this.surveyStore.passedSteps || 1, 1)
      }
      if (this.isPersonalDataPhase || this.isObjectTypePhase) {
        return 2
      }
      return (this.surveyStore.currentConfig?.steps?.length || 1) + 2
    },
    currentStepNumber() {
      if (this.isOutro) {
        return this.totalSteps
      }
      if (this.surveyStore.currentConfig.name === 'personal_data') {
        return 1
      }
      if (this.surveyStore.currentConfig.name === 'objekttyp') {
        return 2
      }
      return Math.min(this.surveyStore.currentStep + 3, this.totalSteps)
    },
    progressPercent() {
      return Math.round((this.currentStepNumber / this.totalSteps) * 100)
    },
    progressLabel() {
      return this.isOutro ? 'Abgeschlossen' : `Schritt ${this.currentStepNumber} von ${this.totalSteps}`
    },
    objectCreationChipTitle() {
      if (this.isPersonalDataPhase || this.isObjectTypePhase) {
        return 'Objektdaten'
      }

      return this.currentStageLabel
    },
    setupChipSubtitle() {
      return this.setupPhaseCompleted ? 'Abgeschlossen' : '1 von 2'
    },
    objectCreationChipSubtitle() {
      if (this.isPersonalDataPhase) {
        return 'Als Nächstes: Art des Eintrags'
      }

      if (this.isObjectTypePhase) {
        return 'Objektdaten'
      }

      if (this.objectCreationPhaseCompleted) {
        return 'Abgeschlossen'
      }

      if (this.showSubmit && this.currentStepNumber >= this.totalSteps) {
        return 'Abgeschlossen'
      }

      return this.progressLabel
    },
    accommodationStepLabel() {
      return this.surveyStore.data.objekttyp === 'unterkunft' ? 'Weitere Unterkünfte' : 'Unterkünfte'
    },
    currentStageLabel() {
      if (this.isOutro) {
        return 'Fertiggestellt'
      }
      if (this.surveyStore.currentConfig.name === 'personal_data') {
        return 'Persönliche Daten'
      }
      if (this.surveyStore.currentConfig.name === 'objekttyp') {
        return 'Art des Eintrags auswählen'
      }
      return this.formatStageLabel(this.getStepName())
    },
    nextStepLabel() {
      if (this.showSubmit) {
        return 'Prüfen und Fragebogen absenden'
      }

      if (!this.surveyStore.currentConfig?.steps?.length) {
        return 'Weiter zur nächsten Frage'
      }

      const nextStep = this.surveyStore.currentConfig.steps[this.surveyStore.currentStep + 1]
      if (!nextStep) {
        return 'Prüfen und Fragebogen absenden'
      }

      const nextStepKey = Object.entries(nextStep).find(([, value]) => value?.component)?.[0]
      return nextStepKey ? this.formatStageLabel(nextStepKey) : 'Weiter zur nächsten Frage'
    },
    showSubmit() {
      if (this.showNavSubmit) {
        return true
      }
      return this.surveyStore.data.zusatzunterkunft.select === 'false' && this.getStepName() === 'zusatzunterkunft'
    }
  },
  watch: {}
}
</script>

<style scoped lang="scss">
.survey-shell__header {
  max-width: 100%;
}

.survey-step-chip {
  min-width: 220px;
  padding: 1rem 1.25rem;
  border: 1px solid rgba(13, 110, 253, 0.12);
  background: rgba(255, 255, 255, 0.88);
  box-shadow: 0 1rem 2.5rem rgba(15, 23, 42, 0.08);
}

.survey-step-chip--completed {
  border-color: rgba(25, 135, 84, 0.2);
  background: rgba(25, 135, 84, 0.08);
}

.survey-step-chip--active {
  border-color: rgba(13, 110, 253, 0.18);
}

.survey-progress {
}

.survey-form {
  max-width: 100%;
}

.survey-nav {
  position: sticky;
  bottom: 1rem;
  z-index: 1010;
}

.survey-nav::before {
  content: "";
  position: absolute;
  inset: -1.5rem 0 100%;
  pointer-events: none;
  background: linear-gradient(180deg, rgba(237, 244, 248, 0), rgba(237, 244, 248, 0.92));
}

.survey-nav__surface {
  position: relative;
  padding: 1rem;
  color: #ffffff;
  background: #16324a;
  border: 1px solid rgba(255, 255, 255, 0.16);
  box-shadow: 0 1.25rem 2.5rem rgba(10, 33, 48, 0.26), 0 0 0 1px rgba(10, 33, 48, 0.12);
}

.survey-nav__main {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.survey-nav__next {
  min-width: 0;
  padding-left: 0.85rem;
  border-left: 3px solid #30b9ec;
}

.survey-nav__eyebrow {
  margin-bottom: 0.15rem;
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
}

.survey-nav__title {
  color: #ffffff;
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.25;
}

.survey-nav__controls {
  display: flex;
  flex: 0 0 auto;
  gap: 0.5rem;
}

.survey-nav__direction-button {
  min-height: 2.75rem;
  padding-inline: 1.25rem;
  font-weight: 700;
}

.survey-nav__direction-button.btn-outline-secondary {
  color: #ffffff;
  border-color: rgba(255, 255, 255, 0.58);
  background: rgba(255, 255, 255, 0.08);
}

.survey-nav__direction-button.btn-outline-secondary:hover,
.survey-nav__direction-button.btn-outline-secondary:focus-visible {
  color: #16324a;
  border-color: #ffffff;
  background: #ffffff;
}

.survey-nav__direction-button.btn-primary {
  color: #16324a;
  border-color: #ffffff;
  background: #ffffff;
  box-shadow: 0 0.5rem 1rem rgba(10, 33, 48, 0.2);
}

.survey-nav__direction-button.btn-primary:hover,
.survey-nav__direction-button.btn-primary:focus-visible {
  color: #102637;
  border-color: #d7f2fb;
  background: #d7f2fb;
}

.survey-nav__errors {
  max-height: 9rem;
  overflow-y: auto;
}

.survey-nav__error-disclosure,
.survey-nav__single-error {
  border: 1px solid rgba(220, 53, 69, 0.28);
  background: #f8dfe2;
  color: #6d252d;
}

.survey-nav__single-error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.55rem 0.7rem;
  font-size: 0.9rem;
  font-weight: 600;
}

.survey-nav__error-summary {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.55rem 0.7rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  list-style: none;

  &::-webkit-details-marker {
    display: none;
  }

  &::marker {
    content: "";
  }
}

.survey-nav__error-details {
  max-height: 7rem;
  overflow-y: auto;
  padding: 0 0.7rem 0.6rem;
  font-size: 0.85rem;
}

@media (max-width: 575.98px) {
  .survey-nav {
    bottom: 0.5rem;
  }

  .survey-nav__surface {
    padding: 0.85rem;
  }

  .survey-nav__main {
    align-items: stretch;
    flex-direction: column;
  }

  .survey-nav__controls {
    width: 100%;
  }

  .survey-nav__direction-button {
    flex: 1 1 0;
    padding-inline: 1rem !important;
  }
}

.progress {
  height: 0.7rem;
  background: var(--bs-secondary-bg);
}

:deep(.survey-panel) {
  border: 0;
  box-shadow: 0 1rem 2.5rem rgba(15, 23, 42, 0.08);
  overflow: hidden;
}

:deep(.survey-panel .card-body) {
  padding: 1.5rem;
}

:deep(.survey-shell .card),
:deep(.survey-shell .alert),
:deep(.survey-shell .btn),
:deep(.survey-shell .form-select),
:deep(.survey-shell .form-control),
:deep(.survey-shell .badge),
:deep(.survey-shell .progress),
:deep(.survey-shell .list-group-item) {
  border-radius: 0 !important;
}

@media (min-width: 992px) {
  :deep(.survey-panel .card-body) {
    padding: 2rem;
  }
}
</style>
